import { createContext, ReactNode } from 'react';

import { Draft } from '../components/RequestDrafts/types';
import { useApplyDraft } from '../hooks/requestDraft/useApplyDraft';
import { ApplyPlan, useApplyPlan } from '../hooks/requestDraft/useApplyPlan';
import { useDeleteDraft } from '../hooks/requestDraft/useDeleteDraft';
import { useDiscardDraft } from '../hooks/requestDraft/useDiscardDraft';
import { useRequestDraft } from '../hooks/requestDraft/useRequestDraft';

type Context = {
  draft?: Draft;
  isPending: boolean;
  isBookingConfirmation: boolean;
  isUnsupportedRoute: boolean;

  applyPlan?: ApplyPlan;
  isApplying: boolean;
  applyDraft: () => Promise<void>;

  isDiscarding: boolean;
  isDeleting: boolean;
  discardDraft: (reason: string) => Promise<void>;
  deleteDraft: () => Promise<void>;
};

export const CurrentDraftContext = createContext({} as Context);

type Props = {
  draftId: number | null;
  onResolved: () => void;
  children: ReactNode;
};

/**
 * Owns everything stateful about the draft under review: the query, the apply plan/mutation, and the discard/delete
 * flows. Components in components/RequestDrafts are purely visual and read from this context.
 */
export const CurrentDraftProvider = ({ draftId, onResolved, children }: Props) => {
  const { draft, isPending, isBookingConfirmation, isUnsupportedRoute } = useRequestDraft(draftId);

  const applyPlan = useApplyPlan(draft);
  const [applyDraft, isApplying] = useApplyDraft(draft);
  const [discardDraft, isDiscarding] = useDiscardDraft(draft, onResolved);
  const [deleteDraft, isDeleting] = useDeleteDraft(draft, onResolved);

  return (
    <CurrentDraftContext.Provider
      value={{
        draft,
        isPending,
        isBookingConfirmation,
        isUnsupportedRoute,
        applyPlan,
        isApplying,
        applyDraft,
        isDiscarding,
        isDeleting,
        discardDraft,
        deleteDraft,
      }}
    >
      {children}
    </CurrentDraftContext.Provider>
  );
};
